import React, { useMemo, useState } from 'react';
import { Add, ChevronLeft, ChevronRight } from '@mui/icons-material';
import {
  Alert,
  Badge,
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { Row } from '../../atoms/row';
import { addMonths, endOfMonth, startOfMonth } from 'date-fns';
import { RouterOutput, trpc } from '../../api/trpc';
import { ScheduleRemove } from './schedule.remove';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { jsDateToIsoDate } from '../../utils/isodate';
import { ScheduleForm } from './schedules.form';
import { Plus } from '../../atoms/icons/plus';
import { Calendar } from '../../atoms/icons/calendar';
import { CalendarRecurring } from '../../atoms/icons/calendar-recurring';
import { ScheduleLabel } from './schedule.label';
import { ScheduleType, scheduleTypes } from './schedules.types';

type Item = RouterOutput['schedules']['list'][number];

type Props = {
  /** Schedule actions this planner lists and can create. Defaults to all. */
  types?: readonly ScheduleType[];
  title?: React.ReactNode;
  description?: React.ReactNode;
};

export const Schedules = ({
  types = scheduleTypes,
  title,
  description,
}: Props) => {
  const [open, setOpen] = useState<string | undefined>(undefined);

  const [from, setFrom] = useState(startOfMonth(new Date()));
  const to = useMemo(() => endOfMonth(from), [from]);
  const [includeProcessed, setIncludeProcessed] = useState(false);

  const onPrev = () => {
    setFrom(startOfMonth(addMonths(from, -1)));
  };

  const onNext = () => {
    setFrom(startOfMonth(addMonths(from, 1)));
  };

  const {
    data: schedules,
    isLoading,
    isError,
  } = trpc.schedules.list.useQuery({
    from: jsDateToIsoDate(from),
    to: jsDateToIsoDate(to),
    includeProcessed,
    types: [...types] as [ScheduleType, ...ScheduleType[]],
  });

  return (
    <Container sx={{ pt: 1, pb: 2 }}>
      <Row sx={{ mb: 0.5, gap: 0.5 }}>
        <Typography variant="h2">
          {title ?? (
            <FormattedMessage id="schedules.title" defaultMessage="Plánovač" />
          )}

          <Plus sx={{ ml: 1 }} />
        </Typography>
      </Row>

      <Typography variant="body1" sx={{ mb: 1.25 }}>
        {description ?? (
          <FormattedMessage
            id="schedules.description"
            defaultMessage="Naplánujte si vybití nebo nabití baterie podle potřeby."
          />
        )}
      </Typography>

      <Paper>
        <Row gap={1} sx={{ mb: 2 }}>
          <IconButton size="small" onClick={onPrev}>
            <ChevronLeft />
          </IconButton>
          <Box>
            <FormattedDate value={from} year="numeric" month="long" />
          </Box>
          <IconButton size="small" onClick={onNext}>
            <ChevronRight />
          </IconButton>

          <Button
            onClick={() => setOpen('new')}
            variant="outlined"
            size="small"
            sx={{ ml: 'auto' }}
            startIcon={<Add />}
          >
            <FormattedMessage id="schedules.add" defaultMessage="Přidat plán" />
          </Button>
        </Row>
        <Row gap={1} sx={{ mb: 1 }}>
          <Box sx={{ flex: 1, textAlign: 'right' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeProcessed}
                  onChange={(e) => setIncludeProcessed(e.target.checked)}
                />
              }
              label={
                <FormattedMessage
                  id="schedules.show_processed"
                  defaultMessage="Zobrazit provedené"
                />
              }
              sx={{
                mr: 0,
                '& .MuiTypography-root': { fontSize: '0.7rem' },
              }}
            />
          </Box>
        </Row>
        {schedules && schedules.length ? (
          <Stack sx={{ gap: 2 }}>
            {schedules.map((schedule) => {
              return <ScheduleItem key={schedule.id} schedule={schedule} />;
            })}
          </Stack>
        ) : !isLoading ? (
          <Typography sx={{ p: 2, textAlign: 'center' }}>
            <FormattedMessage
              id="schedules.no_plans_found"
              defaultMessage="Žádné plány nebyly nalezeny."
            />
          </Typography>
        ) : null}
        {isLoading && (
          <>
            <Skeleton />
            <Skeleton />
            <Skeleton />
          </>
        )}

        {isError && (
          <Alert severity="error">
            <FormattedMessage
              id="schedules.loading.error"
              defaultMessage="Chyba při načítání plánů"
            />
          </Alert>
        )}
      </Paper>

      <ScheduleForm
        open={Boolean(open)}
        onClose={() => setOpen(undefined)}
        types={types}
      />
    </Container>
  );
};

const ScheduleItem = ({ schedule }: { schedule: Item }) => {
  const { id, active, processed, missed, parentId } = schedule;

  const status = missed
    ? 'missed'
    : processed
    ? 'processed'
    : active
    ? 'active'
    : 'scheduled';

  return (
    <Row
      key={id}
      sx={{
        alignItems: 'stretch',
        '&:not(:last-child)': { borderBottom: 'solid 1px #ECECEC', pb: 2 },
      }}
      gap={1}
    >
      <Box sx={{ mb: 'auto', pr: { xs: 1.25, sm: 2.5 } }}>
        <Badge
          color={
            status === 'processed'
              ? undefined
              : status === 'missed'
              ? 'warning'
              : status === 'active'
              ? 'info'
              : 'primary'
          }
          variant="dot"
        >
          {parentId ? (
            <CalendarRecurring
              sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
            />
          ) : (
            <Calendar sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
          )}
        </Badge>
      </Box>
      <Box sx={{ flex: 1 }}>
        <ScheduleLabel schedule={schedule} />
      </Box>

      <Stack sx={{ textAlign: 'right' }}>
        {status !== 'processed' && (
          <Box>
            <ScheduleRemove schedule={schedule} />
          </Box>
        )}

        <Typography
          variant="body1"
          sx={{
            mt: 'auto',
            color:
              status === 'processed'
                ? undefined
                : status === 'missed'
                ? 'warning.main'
                : status === 'active'
                ? 'info.main'
                : 'primary.main',
          }}
        >
          {status === 'processed' && (
            <FormattedMessage
              id="schedules.processed"
              defaultMessage="Proběhlo"
            />
          )}
          {status === 'missed' && (
            <FormattedMessage
              id="schedules.missed"
              defaultMessage="Neproběhlo"
            />
          )}
          {status === 'active' && (
            <FormattedMessage id="schedules.running" defaultMessage="Probíhá" />
          )}
          {status === 'scheduled' && (
            <FormattedMessage
              id="schedules.scheduled"
              defaultMessage="Naplánováno"
            />
          )}
        </Typography>
      </Stack>
    </Row>
  );
};
